/**
 * LoginPage.tsx — Fullscreen Login
 *
 * Background: Full-screen point cloud animation
 * Overlay:    Glass-pane login form (bottom-right)
 * Logo:       Top-left corner
 * Claims:     Bottom-left, rotating facts
 */

import React, { useState, useMemo, useEffect } from 'react'
import { useAuthStore } from '../../store/auth'
import { PointCloudAnimation } from './PointCloudAnimation'

const BUILD_VERSION = 'V4.12 · BUILD 2026.04.B'

const CLAIMS: { fact: string; source: string }[] = [
  { fact: '374 Mio. Tonnen Bauschutt entstehen jährlich in der EU — Bestandsdokumentation ist der erste Schritt, das zu ändern.', source: 'Eurostat' },
  { fact: 'Sanierung statt Abriss spart bis zu 75 % der CO₂-Emissionen. Es beginnt mit dem Verstehen, was da ist.', source: 'Architecture 2030' },
  { fact: 'Nur 1 % aller Baumaterialien werden wiederverwendet. Wir dokumentieren, was bleiben kann.', source: 'Ellen MacArthur Foundation' },
  { fact: '40 % aller weltweit gewonnenen Rohstoffe fließen in Gebäude. Jedes davon verdient eine digitale Bestandsaufnahme.', source: 'UNEP' },
  { fact: 'Gebäude verursachen 37 % der globalen CO₂-Emissionen. Die graue Energie im Bestand ist bereits investiert.', source: 'UNEP Global Status Report 2023' },
  { fact: '220 Mio. Tonnen mineralische Bauabfälle entstehen jährlich allein in Deutschland.', source: 'Umweltbundesamt' },
  { fact: '70 % des gesamten Abfallaufkommens in der EU stammen aus dem Bausektor.', source: 'Europäische Kommission' },
  { fact: 'Ein Gebäudeabriss vernichtet durchschnittlich 50 Jahre gespeicherter Energie. Dokumentation schützt diesen Wert.', source: 'DGNB' },
]

type Mode = 'credentials' | 'access-token'

export function LoginPage() {
  const [mode, setMode] = useState<Mode>('credentials')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [accessTokenInput, setAccessTokenInput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const login = useAuthStore((s) => s.login)
  const loginWithAccessToken = useAuthStore((s) => s.loginWithAccessToken)

  // Rotierender Claim-Ticker
  const startIndex = useMemo(() => Math.floor(Math.random() * CLAIMS.length), [])
  const [claimIndex, setClaimIndex] = useState(startIndex)
  const [claimVisible, setClaimVisible] = useState(false)

  useEffect(() => {
    const showTimer = setTimeout(() => setClaimVisible(true), 2000)
    const interval = setInterval(() => {
      setClaimVisible(false)
      setTimeout(() => {
        setClaimIndex((i) => (i + 1) % CLAIMS.length)
        setClaimVisible(true)
      }, 600)
    }, 9000)
    return () => { clearTimeout(showTimer); clearInterval(interval) }
  }, [])

  const claim = CLAIMS[claimIndex]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      if (mode === 'credentials') {
        await login(email, password)
      } else {
        await loginWithAccessToken(accessTokenInput.trim())
      }
    } catch (err: any) {
      setError(err.message || 'Anmeldung fehlgeschlagen')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="login-page">
      {/* ── Fullscreen Animation ────────────────────────── */}
      <div className="login-page__bg">
        <PointCloudAnimation />
      </div>

      {/* ── Logo (top-left) ─────────────────────────────── */}
      <div className="login-page__logo-wrap">
        <img className="login-page__logo" src="/assets/logo_black.svg" alt="orto" />
        <span className="login-page__version">{BUILD_VERSION}</span>
      </div>

      {/* ── Claims (bottom-left) ────────────────────────── */}
      <div className={`login-page__claim ${claimVisible ? 'login-page__claim--visible' : ''}`}>
        <p className="login-page__claim-text">{claim.fact}</p>
        <span className="login-page__claim-source">{claim.source}</span>
      </div>

      {/* ── Glass Pane Form (bottom-right) ──────────────── */}
      <div className="login-page__pane">
        <form className="login-page__form" onSubmit={handleSubmit}>
          {error && <div className="login-page__error">{error}</div>}

          <div className="login-page__fields">
            {mode === 'credentials' ? (
              <>
                <input
                  className="login-page__input"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="E-Mail"
                />
                <input
                  className="login-page__input"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Passwort"
                />
              </>
            ) : (
              <input
                className="login-page__input"
                type="text"
                value={accessTokenInput}
                onChange={(e) => setAccessTokenInput(e.target.value)}
                required
                autoFocus
                placeholder="Access Token"
                style={{ fontFamily: 'monospace' }}
              />
            )}
          </div>

          <button className="login-page__submit" type="submit" disabled={loading}>
            <span>{loading ? 'Wird angemeldet …' : 'Anmelden'}</span>
            {!loading && (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14" /><path d="m12 5 7 7-7 7" />
              </svg>
            )}
          </button>

          <div className="login-page__divider">
            <span>·</span>
          </div>

          <div className="login-page__alt-row" style={{ display: 'flex', gap: 8 }}>
            <button className="login-page__sso" type="button" onClick={() => {}} style={{ flex: 1 }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              <span>Mit Single-Sign-On</span>
            </button>

            <button
              className="login-page__sso"
              type="button"
              onClick={() => { setMode(mode === 'access-token' ? 'credentials' : 'access-token'); setError(null) }}
              style={{ flex: 1 }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="12" r="4" /><path d="M13 12h8" /><path d="M17 12v3" /><path d="M21 12v2" />
              </svg>
              <span>{mode === 'access-token' ? 'Mit E-Mail & Passwort' : 'Mit Access Token'}</span>
            </button>
          </div>
        </form>

        <div className="login-page__pane-footer">
          <a className="login-page__footer-link" href="#" onClick={e => e.preventDefault()}>Passwort vergessen?</a>
          <div className="login-page__footer-links">
            <a href="#">Impressum</a>
            <span>·</span>
            <a href="#">Datenschutz</a>
          </div>
        </div>
      </div>
    </div>
  )
}
